import { Box, HStack, Text } from '@chakra-ui/react';
import { ReactElement } from 'react';
import { CloseIcon } from 'assets/icons/v2';
import { NovaIcon } from 'components/ui/novaIcon';

type OptionWithLabel = {
  value: string;
  label: string;
};

type MultiSelectChipsProps = {
  selectedOptions: OptionWithLabel[];
  maxVisibleChips: number;
  onRemove: (value: string, e: React.MouseEvent) => void;
};

/**
 * MultiSelectChips component - Displays selected items as removable chips
 */
export const MultiSelectChips = ({
  selectedOptions,
  maxVisibleChips,
  onRemove,
}: MultiSelectChipsProps): ReactElement => {
  const visibleChips = selectedOptions.slice(0, maxVisibleChips);
  const remainingCount = selectedOptions.length - maxVisibleChips;

  return (
    <HStack
      gap="xs"
      flex="1"
      width="100%"
      overflowX="auto"
      overflowY="hidden"
      pr="35px"
      css={{
        '&::-webkit-scrollbar': {
          height: '4px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#E5E5E5',
          borderRadius: '2px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#CCCCCC',
        },
      }}
    >
      {visibleChips.map((option) => (
        <Box
          key={option.value}
          bg="tag.background.green"
          h="28px"
          px="xs"
          py="3px"
          borderRadius="s"
          display="flex"
          alignItems="center"
          gap="xs"
          onClick={(e) => e.stopPropagation()}
          flexShrink={0}
        >
          <Text textStyle="body.s.regular" color="text.accent" lineHeight="1.3" whiteSpace="nowrap">
            {option.label}
          </Text>
          <Box
            as="button"
            onClick={(e: React.MouseEvent) => onRemove(option.value, e)}
            display="flex"
            alignItems="center"
            justifyContent="center"
            cursor="pointer"
            _hover={{ opacity: '70' }}
            w="16px"
            h="16px"
            flexShrink={0}
          >
            <NovaIcon icon={CloseIcon} size="s" color="text.accent" aria-hidden />
          </Box>
        </Box>
      ))}
      {remainingCount > 0 && (
        <Box
          bg="tag.background.green"
          h="28px"
          px="xs"
          py="3px"
          borderRadius="s"
          display="flex"
          alignItems="center"
          flexShrink={0}
        >
          <Text textStyle="body.s.regular" color="text.accent" lineHeight="1.3" whiteSpace="nowrap">
            +{remainingCount} more
          </Text>
        </Box>
      )}
    </HStack>
  );
};
